import {
    type FC,
    type PropsWithChildren,
    type ReactNode,
    type RefObject,
    Fragment,
    useContext,
    useEffect,
    useLayoutEffect,
    useImperativeHandle,
    useRef,
    forwardRef,
} from 'react';
import classnames from 'classnames';

import { type CustomScrollEvent, CustomScrollContext } from '@hh.ru/magritte-internal-custom-scroll';
import { Divider } from '@hh.ru/magritte-ui-divider';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { NavigationBarItem } from '@hh.ru/magritte-ui-navigation-bar/NavigationBarItem';
import { NavigationBarItems } from '@hh.ru/magritte-ui-navigation-bar/NavigationBarItems';
import { initAnimations, applyAnimations, QA } from '@hh.ru/magritte-ui-navigation-bar/animationUtils';
import type {
    NavigationBarProps,
    NavigationBarComponentProps,
    NavigationBarInternalState,
} from '@hh.ru/magritte-ui-navigation-bar/types';
import { updateScrollGroundPos } from '@hh.ru/magritte-ui-navigation-bar/updateScrollGroundPos';
import { ProgressBar } from '@hh.ru/magritte-ui-progress-bar';

import styles from './navigation-bar.less';

const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) {
        return null;
    }

    let parentElement = element.parentElement;
    while (parentElement !== null) {
        const { overflowY } = window.getComputedStyle(parentElement);
        if (overflowY.includes('auto') || overflowY.includes('scroll')) {
            return parentElement;
        }
        parentElement = parentElement.parentElement;
    }

    return null;
};

const renderStringNode = (node: ReactNode | ((beforeScroll: boolean) => ReactNode), beforeScroll: boolean) => {
    return typeof node === 'function' ? node(beforeScroll) : node;
};

const StandardTitle = forwardRef<HTMLDivElement, PropsWithChildren<NavigationBarProps> & { 'data-qa'?: string }>(
    ({ left, right, ellipsisRenderFunc, 'data-qa': dataQa, children }, ref) => {
        return (
            <div className={styles.spread} data-qa={dataQa} role="menu" ref={ref}>
                <div className={styles.left}>{left && <NavigationBarItem role="menuitem" item={left} />}</div>
                <div className={styles.right}>
                    <div className={styles.halfTitleWrapper}>{children}</div>
                    <div className={styles.rightDummy}>
                        <NavigationBarItems items={right} size="standard" renderFunc={ellipsisRenderFunc} />
                    </div>
                </div>
            </div>
        );
    }
);

StandardTitle.displayName = 'StandardTitle';

const NavigationBarLayer: FC<PropsWithChildren> = ({ children }) => {
    return <Layer layer="navigation-bar">{children}</Layer>;
};

export const NavigationBarComponent: FC<NavigationBarComponentProps> = ({
    size = 'standard',
    title,
    subtitle,
    left,
    progress,
    right,
    options,
    allowOptionsScroll = false,
    showDivider = false,
    ellipsisRenderFunc,
    inLayer,
    forwardedRef,
    transformTriggerPosition = 'start',
}) => {
    const scrollContext = useContext(CustomScrollContext);

    const stateRef = useRef<NavigationBarInternalState>({
        size,
        heightObservers: [],
        transitionProgress: 0,
        animationProgress: 0,
        skipFirstResizeCallback: true,
        scrollGroundPos: 0,
        currentScrollTop: 0,
        maxScrollTop: 0,
        allowOptionsScroll,
        transformTriggerPosition,
        containerObserver: null,
        sizes: {
            itemsContainer: 0,
            largeTitle: 0,
            standardTitle: 0,
            optionsContainer: 0,
        },
        animations: [],
    });

    stateRef.current.allowOptionsScroll = allowOptionsScroll;
    stateRef.current.size = size;
    stateRef.current.transformTriggerPosition = transformTriggerPosition;

    const itemsContainerRef = useRef<HTMLDivElement>(null);
    const titleBeforeScrollRef = useRef<HTMLDivElement>(null);
    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const dividerRef = useRef<HTMLDivElement>(null);
    const titleAfterScrollRef = useRef<HTMLDivElement>(null);
    const standardItemsRef = useRef<HTMLDivElement>(null);
    const largeItemsRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const backgroundContainerRef = useRef<HTMLDivElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const scrollContainerRef = useRef<HTMLElement | null | undefined>();

    useEffect(() => {
        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (stateRef.current.skipFirstResizeCallback) {
                    stateRef.current.skipFirstResizeCallback = false;
                    return;
                }
                stateRef.current.heightObservers.forEach((observer) => observer());
            });
        }

        const resizeObserver = resizeObserverRef.current;
        const titleContainer = titleBeforeScrollRef.current;
        const optionsContainer = optionsContainerRef.current;

        titleContainer && resizeObserver.observe(titleContainer);
        optionsContainer && resizeObserver.observe(optionsContainer);

        return () => {
            titleContainer && resizeObserver.unobserve(titleContainer);
            optionsContainer && resizeObserver.unobserve(optionsContainer);
        };
    }, [size]);

    useLayoutEffect(() => {
        stateRef.current.containerObserver?.disconnect();
        stateRef.current.containerObserver = null;
        stateRef.current.scrollGroundPos = 0;
        const sizes = stateRef.current.sizes;

        sizes.standardTitle = titleAfterScrollRef.current?.clientHeight ?? 0;
        sizes.optionsContainer = optionsContainerRef.current?.clientHeight ?? 0;
        sizes.itemsContainer = itemsContainerRef.current?.clientHeight ?? 0;
        sizes.largeTitle = size === 'large' ? (titleBeforeScrollRef.current?.clientHeight ?? 0) : 0;

        initAnimations(
            stateRef.current,
            backgroundContainerRef,
            titleBeforeScrollRef,
            titleAfterScrollRef,
            optionsContainerRef,
            largeItemsRef,
            standardItemsRef,
            dividerRef
        );

        const handleScroll = (scrollTop: number, forceRunCallbacks: boolean = false) => {
            updateScrollGroundPos(
                stateRef.current,
                scrollTop,
                scrollContainerRef as RefObject<HTMLElement>,
                wrapperRef
            );
            applyAnimations(stateRef.current, forceRunCallbacks);
            if (
                dividerRef.current &&
                (showDivider === 'with-scroll' || (size === 'large' && showDivider !== 'always'))
            ) {
                dividerRef.current.classList.toggle(
                    styles.invisibleElement,
                    stateRef.current.transitionProgress >= scrollTop
                );
            }
        };

        stateRef.current.handleScroll = handleScroll;

        if (scrollContext !== null) {
            const handleCustomScroll = (event: CustomScrollEvent) => {
                stateRef.current.maxScrollTop = event.maxScrollTop;
                handleScroll(event.scrollTop);
            };
            scrollContainerRef.current = scrollContext.wrapperRef.current;
            scrollContext.subscribe(handleCustomScroll);
            return () => scrollContext.unsubscribe(handleCustomScroll);
        }

        scrollContainerRef.current = findScrollContainer(wrapperRef.current);

        let getScrollTop = () => 0;
        if (scrollContainerRef.current !== null) {
            const scrollContainer = scrollContainerRef.current;
            stateRef.current.maxScrollTop = scrollContainer.scrollHeight - scrollContainer.offsetHeight;
            getScrollTop = () => scrollContainer.scrollTop;
        } else {
            stateRef.current.maxScrollTop =
                document.documentElement.scrollHeight - (visualViewport as VisualViewport).height;
            getScrollTop = () => (visualViewport as VisualViewport).pageTop;
        }

        stateRef.current.currentScrollTop = getScrollTop();
        const handleNativeScroll = () => handleScroll(getScrollTop());
        const scrollContainer = scrollContainerRef.current || window;
        scrollContainerRef.current = scrollContainerRef.current ?? document.documentElement;
        scrollContainer.addEventListener('scroll', handleNativeScroll);
        return () => scrollContainer.removeEventListener('scroll', handleNativeScroll);
    }, [size, allowOptionsScroll, showDivider, scrollContext, options, transformTriggerPosition]);

    // вызываем обработку скролла на каждый ререндер чтобы применились анимации, изменения data-qa и pointer-events в
    // соответствии с фактическим состоянием NavigationBar
    useLayoutEffect(() => {
        stateRef.current.handleScroll?.(stateRef.current.currentScrollTop, true);
    });

    useImperativeHandle(forwardedRef, () => {
        return {
            /**
             * Фактическая высота, которую компонент занимает в родительском контейнере
             * Может отличаться от видимой высоты
             */
            get layoutHeight() {
                return wrapperRef.current?.clientHeight || 0;
            },
            /**
             * Высота видимой части компонента
             * Может отличаться от фактической высоты
             */
            get visibleHeight() {
                return this.layoutHeight - stateRef.current.transitionProgress;
            },
            addHeightObserver: (observer) => {
                stateRef.current.heightObservers.push(observer);
            },
            removeHeightObserver: (observer) => {
                stateRef.current.heightObservers = stateRef.current.heightObservers.filter(
                    (_observer) => _observer !== observer
                );
            },
        };
    }, []);

    const Wrapper = inLayer ? NavigationBarLayer : Fragment;
    const titleProps = { title, subtitle, left, progress, right, ellipsisRenderFunc };
    const titleLarge = title && renderStringNode(title, true);
    const subtitleLarge = subtitle && renderStringNode(subtitle, true);
    const titleStandard = title && renderStringNode(title, false);
    const subtitleStandard = subtitle && renderStringNode(subtitle, false);

    return (
        <Wrapper>
            <div ref={wrapperRef} className={styles.navigationBarWrapper}>
                {/*
                    Класс overflowContainer можно было бы повесить на родительский элемент, но Samsung Browser
                    схлопывает высоту sticky элементов до 0 если для них задан overflow: hidden/clip
                */}
                <div className={styles.overflowContainer}>
                    {/*
                    Отображаем фон в отдельном абсолютно спозиционированном блоке
                    Потому что если каждый элемент будет иметь свой фон, то во время аимации они будут
                    накладываться друг на друга и это будет выглядеть плохо
                */}
                    <div className={styles.backgroundContainer} ref={backgroundContainerRef} />
                    {size === 'large' && (
                        <div className={styles.fixedItemsContainer} ref={itemsContainerRef}>
                            <div className={styles.topPaddingOverride}>
                                <div
                                    className={classnames(styles.spread, styles.pointerEvents)}
                                    data-qa={QA.items}
                                    ref={largeItemsRef}
                                    role="menu"
                                >
                                    <div className={styles.left}>
                                        {left && <NavigationBarItem role="menuitem" item={left} />}
                                    </div>
                                    <div className={styles.right}>
                                        {progress && (
                                            <div className={classnames(styles.progress)}>
                                                <div className={styles.availableWidthWrapper}>
                                                    <ProgressBar {...progress} />
                                                </div>
                                            </div>
                                        )}
                                        <NavigationBarItems
                                            items={right}
                                            size="large"
                                            renderFunc={ellipsisRenderFunc}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div
                        className={classnames({
                            [styles.fixedTitleContainer]: size === 'large',
                            [styles.stickyTitleContainer]: size === 'standard',
                            [styles.pointerEvents]: size === 'standard',
                            [styles.invisibleElement]: size === 'large',
                        })}
                        ref={size === 'large' ? titleAfterScrollRef : null}
                        data-qa={size === 'standard' ? QA.standardTitle : ''}
                    >
                        <div className={styles.topPaddingOverride}>
                            <div className={styles.standardTitleContainer}>
                                <StandardTitle
                                    {...titleProps}
                                    ref={standardItemsRef}
                                    data-qa={size === 'standard' ? QA.items : ''}
                                >
                                    {!!titleStandard && (
                                        <div
                                            className={styles.titleWrapper}
                                            ref={size === 'standard' ? titleAfterScrollRef : null}
                                        >
                                            <div
                                                className={classnames(styles.title, {
                                                    [styles.titleWithoutSubtitle]: !subtitleStandard,
                                                })}
                                            >
                                                {titleStandard}
                                            </div>
                                            {!!subtitleStandard && (
                                                <div className={styles.subtitle}>{subtitleStandard}</div>
                                            )}
                                        </div>
                                    )}
                                    {size === 'standard' && !!titleLarge && (
                                        <div className={styles.titleWrapper} ref={titleBeforeScrollRef}>
                                            <div
                                                className={classnames(styles.title, {
                                                    [styles.titleWithoutSubtitle]: !subtitleLarge,
                                                })}
                                            >
                                                {titleLarge}
                                            </div>
                                            {!!subtitleLarge && <div className={styles.subtitle}>{subtitleLarge}</div>}
                                        </div>
                                    )}
                                </StandardTitle>
                            </div>
                        </div>
                    </div>
                    {size === 'large' && (
                        <div
                            className={classnames(styles.largeTitleContainer, styles.pointerEvents)}
                            data-qa={QA.largeTitle}
                            ref={titleBeforeScrollRef}
                        >
                            {!!titleLarge && (
                                <>
                                    <div className={styles.title}>{titleLarge}</div>
                                    {!!subtitleLarge && <div className={styles.subtitle}>{subtitleLarge}</div>}
                                </>
                            )}
                        </div>
                    )}

                    {options && (
                        <div
                            className={classnames({
                                [styles.overflowContainer]: allowOptionsScroll,
                            })}
                        >
                            <div
                                className={classnames(styles.pointerEvents, styles.options)}
                                data-qa="navigation-bar-options"
                                ref={optionsContainerRef}
                            >
                                {options}
                            </div>
                        </div>
                    )}

                    {(!!showDivider || size === 'large') && (
                        <div
                            ref={dividerRef}
                            className={classnames(styles.dividerContainer, {
                                [styles.invisibleElement]: showDivider !== 'always',
                            })}
                            data-qa="navigation-bar-divider"
                        >
                            <Divider />
                        </div>
                    )}
                </div>
            </div>
        </Wrapper>
    );
};
